import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { addItemToCart, isInCart } from '../utils/cartUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import Navbar from '../components/Navbar';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import SlabRating from '../components/SlabRating';
import { SellerBadges } from '../components/SellerBadges';
import ImageGallery from '../components/ImageGallery';
import RecentSalesTable from '../components/RecentSalesTable';
import { toast } from 'sonner';
import { ArrowLeft, Star, ShoppingCart, Plus, User, Calendar, Tag, MapPin, MessageCircle, Edit2, Trash2, Save, X, Heart, Package, Flag } from 'lucide-react';
import ReportListingModal from '../components/ReportListingModal';

const getGradeClass = (company, isRaw = false) => {
  if (isRaw) return 'bg-orange-500/80 text-white';
  switch (company?.toUpperCase()) {
    case 'PSA': return 'grade-psa';
    case 'BGS': return 'grade-bgs';
    case 'SGC': return 'grade-sgc';
    default: return 'grade-raw';
  }
};

// Check if card is raw (ungraded)
const isRawCard = (card) => {
  return card?.grade_company?.toUpperCase() === 'RAW' || 
         card?.item_specifics?.condition === 'Raw' ||
         card?.item_specifics?.professional_grader === 'Raw (Ungraded)';
};

export const CardDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated, user } = useAuth();
  
  const [card, setCard] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });
  
  // Owner Edit/Delete state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ price: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Watchlist state
  const [isWatched, setIsWatched] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  
  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);

  useEffect(() => {
    fetchCard();
  }, [id]);

  // Check if card is in watchlist
  useEffect(() => {
    const checkWatchlist = async () => {
      if (!isAuthenticated || !id) return;
      try {
        const response = await api().get('/watchlist/ids');
        setIsWatched(response.data.card_ids.includes(id));
      } catch (error) {
        console.error('Failed to check watchlist:', error);
      }
    };
    checkWatchlist();
  }, [id, isAuthenticated]);

  const fetchCard = async () => {
    try {
      const response = await api().get(`/cards/${id}`);
      setCard(response.data);
      
      // Fetch seller profile
      const sellerResponse = await api().get(`/users/${response.data.seller_id}`);
      setSeller(sellerResponse.data);
    } catch (error) {
      console.error('Failed to fetch card:', error);
      toast.error('Card not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    setCheckoutOpen(true);
  };

  const handleCheckout = async () => {
    // Validate shipping address
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip) {
      toast.error('Please fill in all shipping fields');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await api().post('/orders/create-checkout', {
        listing_id: card.id,
        shipping_address: shippingAddress,
        frontend_origin: window.location.origin,
        success_url: `${window.location.origin}/orders/{order_id}?payment=success`,
        cancel_url: `${window.location.origin}/cards/${card.id}?cancelled=true`
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleMessageSeller = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to message sellers');
      navigate('/login');
      return;
    }

    setMessageSending(true);
    try {
      // Start or get existing conversation
      const response = await api().post(`/conversations/start?receiver_id=${card.seller_id}&listing_id=${card.id}`);
      
      // Navigate to messages with this conversation
      navigate(`/messages?conversation=${response.data.conversation_id}`);
    } catch (error) {
      console.error('Message seller error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setMessageSending(false);
    }
  };

  // Owner Edit handlers
  const openEditModal = () => {
    setEditForm({
      price: card.price?.toString() || '',
      description: card.description || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveChanges = async () => {
    const price = parseFloat(editForm.price);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSaving(true);
    try {
      const response = await api().patch(`/cards/${card.id}`, {
        price: price,
        description: editForm.description || null
      });
      
      // Update local card state with response
      setCard(response.data);
      setEditModalOpen(false);
      toast.success('Listing updated successfully!');
    } catch (error) {
      console.error('Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteListing = async () => {
    setDeleting(true);
    try {
      await api().delete(`/cards/${card.id}`);
      toast.success('Listing deleted successfully');
      navigate('/marketplace');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleWatchToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add to watchlist');
      navigate('/login');
      return;
    }
    
    setWatchLoading(true);
    try {
      if (isWatched) {
        await api().delete(`/watchlist/${id}`);
        setIsWatched(false);
        toast.success('Removed from watchlist');
      } else {
        await api().post(`/watchlist/${id}`);
        setIsWatched(true);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      console.error('Watch toggle error:', error);
      toast.error('Failed to update watchlist');
    } finally {
      setWatchLoading(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!card) return null;

  // Calculate totals
  const surcharge = (card.price * 0.035).toFixed(2);
  const tax = (card.price * 0.08).toFixed(2);
  // For cards under $20, use Standard Envelope rate ($3.99), otherwise standard shipping
  const shipping = card.price < 20 ? 3.99 : 5.99;
  const total = (parseFloat(card.price) + parseFloat(surcharge) + parseFloat(tax) + shipping).toFixed(2);

  const isOwnCard = user?.id === card.seller_id;
  const itemInCart = isInCart(id);
  
  function handleAddToCart() {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    const added = addItemToCart(card);
    if (added) {
      toast.success('Added to cart!');
      window.dispatchEvent(new Event('cartUpdated'));
    } else {
      toast.info('Item already in cart');
    }
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Marketplace
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <ImageGallery 
              images={card.images?.length > 0 ? card.images : (card.image_url ? [card.image_url] : [])}
              alt={`${card.player_name} ${card.set_name}`}
            />
            
            {/* Grade/Condition Badge - Dynamic based on Raw vs Graded */}
            {isRawCard(card) ? (
              <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-lg text-sm font-bold bg-orange-500/90 text-white">
                <Package className="w-4 h-4" />
                Raw {card.item_specifics?.raw_condition ? `- ${card.item_specifics.raw_condition}` : ''}
              </div>
            ) : (
              <div className={`inline-block mt-4 px-3 py-1.5 rounded-lg text-sm font-bold ${getGradeClass(card.grade_company)}`}>
                {card.grade_company} {card.grade_value || ''}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-8">
            {/* Title */}
            <div>
              <span className="badge badge-blue mb-3">{card.sport}</span>
              <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase text-white">
                {card.player_name}
              </h1>
              <p className="text-xl text-zinc-400 mt-2">{card.year} {card.set_name}</p>
            </div>

            {/* Price Breakdown */}
            <div className="stat-card p-6 space-y-4">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Card Price</p>
                <p className="font-mono text-4xl font-bold text-emerald-400">
                  ${card.price?.toFixed(2)}
                </p>
              </div>
              
              {/* Fee Breakdown */}
              <div className="pt-4 border-t border-zinc-700/50 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Processing Fee (3.5%)</span>
                  <span className="font-mono text-zinc-300">${(card.price * 0.035).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">
                    {card.price < 20 ? 'Shipping (Standard Envelope)' : 'Shipping (from $5.28)'}
                  </span>
                  <span className="font-mono text-zinc-300">
                    {card.price < 20 ? '$3.99' : '$5.28+'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm pt-2 border-t border-zinc-700/30">
                  <span className="text-zinc-300 font-medium">Est. Total</span>
                  <span className="font-mono font-bold text-white">
                    ${(card.price + (card.price * 0.035) + (card.price < 20 ? 3.99 : 5.28)).toFixed(2)}{card.price >= 20 ? '+' : ''}
                  </span>
                </div>
              </div>
              
              {/* Zero Commission Message */}
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs text-emerald-400">
                  <span className="font-semibold">$0 Slab Society Commission</span> — You only pay for shipping and secure payment processing.
                </p>
              </div>
            </div>

            {/* Seller Info */}
            {seller && (
              <div 
                className="stat-card p-6 cursor-pointer hover:border-white/10 transition-colors"
                onClick={() => navigate(`/profile/${seller.id}`)}
                data-testid="seller-card"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-white">{seller.name}</p>
                      {card.seller_is_founder && (
                        <img 
                          src={card.seller_founder_badge_url} 
                          alt="Founder Elite" 
                          className="w-8 h-auto object-contain" 
                          title="Founder Elite - First 25 Elite Members"
                          style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}
                        />
                      )}
                      {card.seller_is_founder_gold && (
                        <img 
                          src={card.seller_founder_gold_badge_url} 
                          alt="Founder Gold" 
                          className="w-8 h-auto object-contain" 
                          title="Founder Gold - First 25 Gold Members"
                          style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}
                        />
                      )}
                      <SellerBadges 
                        rating={seller.rating || 0} 
                        totalSales={seller.total_sales || 0} 
                        size="sm" 
                      />
                    </div>
                    <p className="text-sm text-zinc-400">@{seller.username}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <SlabRating rating={seller.rating || 0} readonly size="sm" />
                      <span className={`font-medium ${
                        seller.rating >= 4.5 ? 'text-amber-400' : 
                        seller.rating > 0 ? 'text-emerald-400' : 'text-zinc-500'
                      }`}>{seller.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                    <p className="text-xs text-zinc-500">{seller.total_reviews || 0} reviews</p>
                  </div>
                </div>
              </div>
            )}

            {/* Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="stat-card p-4">
                <Calendar className="w-5 h-5 text-zinc-500 mb-2" />
                <p className="text-sm text-zinc-500">Year</p>
                <p className="font-medium text-white">{card.year}</p>
              </div>
              <div className="stat-card p-4">
                <Tag className="w-5 h-5 text-zinc-500 mb-2" />
                <p className="text-sm text-zinc-500">Set</p>
                <p className="font-medium text-white truncate">{card.set_name}</p>
              </div>
            </div>

            {/* Item Specifics Section */}
            {card.item_specifics && Object.keys(card.item_specifics).length > 0 && (
              <div className="stat-card p-6" data-testid="item-specifics-section">
                <h3 className="font-heading font-bold text-lg text-white mb-4 uppercase tracking-wide">Item Specifics</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {card.item_specifics.brand_product && (
                    <>
                      <p className="text-sm text-zinc-500">Brand/Product</p>
                      <p className="text-sm font-medium text-white">{card.item_specifics.brand_product}</p>
                    </>
                  )}
                  {card.item_specifics.card_number && (
                    <>
                      <p className="text-sm text-zinc-500">Card Number</p>
                      <p className="text-sm font-medium text-white">#{card.item_specifics.card_number}</p>
                    </>
                  )}
                  {card.item_specifics.professional_grader && (
                    <>
                      <p className="text-sm text-zinc-500">{isRawCard(card) ? 'Card Type' : 'Professional Grader'}</p>
                      <p className={`text-sm font-medium ${isRawCard(card) ? 'text-orange-400' : 'text-white'}`}>
                        {card.item_specifics.professional_grader}
                      </p>
                    </>
                  )}
                  {/* Show Grade only for graded cards */}
                  {!isRawCard(card) && card.item_specifics.grade && (
                    <>
                      <p className="text-sm text-zinc-500">Grade</p>
                      <p className="text-sm font-medium text-white">{card.item_specifics.grade}</p>
                    </>
                  )}
                  {/* Show Cert Number only for graded cards */}
                  {!isRawCard(card) && card.item_specifics.cert_number && (
                    <>
                      <p className="text-sm text-zinc-500">Certification #</p>
                      <p className="text-sm font-medium text-blue-400">{card.item_specifics.cert_number}</p>
                    </>
                  )}
                  {/* Show Raw Condition only for raw cards */}
                  {isRawCard(card) && card.item_specifics.raw_condition && (
                    <>
                      <p className="text-sm text-zinc-500">Raw Condition</p>
                      <p className="text-sm font-medium text-orange-400">{card.item_specifics.raw_condition}</p>
                    </>
                  )}
                  {card.item_specifics.is_autographed && (
                    <>
                      <p className="text-sm text-zinc-500">Autographed</p>
                      <p className="text-sm font-medium text-amber-400">
                        Yes {card.item_specifics.signer_name && `- ${card.item_specifics.signer_name}`}
                        {card.item_specifics.auto_authentication && ` (${card.item_specifics.auto_authentication})`}
                      </p>
                    </>
                  )}
                  {card.item_specifics.is_serial_numbered && (
                    <>
                      <p className="text-sm text-zinc-500">Serial Numbered</p>
                      <p className="text-sm font-medium text-blue-400">
                        {card.item_specifics.serial_number || '?'}/{card.item_specifics.total_print_run || '?'}
                      </p>
                    </>
                  )}
                  {card.item_specifics.parallel_variety && (
                    <>
                      <p className="text-sm text-zinc-500">Parallel/Variety</p>
                      <p className="text-sm font-medium text-purple-400">{card.item_specifics.parallel_variety}</p>
                    </>
                  )}
                  {card.item_specifics.is_rookie_card && (
                    <>
                      <p className="text-sm text-zinc-500">Rookie Card</p>
                      <p className="text-sm font-bold text-emerald-400">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/30">
                          RC
                        </span>
                      </p>
                    </>
                  )}
                  {card.item_specifics.condition && (
                    <>
                      <p className="text-sm text-zinc-500">Type</p>
                      <p className={`text-sm font-medium ${isRawCard(card) ? 'text-orange-400' : 'text-white'}`}>
                        {card.item_specifics.condition}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {card.description && (
              <div>
                <h3 className="font-medium text-white mb-2">Description</h3>
                <p className="text-zinc-400">{card.description}</p>
              </div>
            )}

            {/* Recent Sales / Market Value */}
            <RecentSalesTable cardId={id} />

            {/* Buy Button */}
            {!isOwnCard && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button 
                    onClick={handleBuyNow}
                    className="flex-1 btn-primary h-14 text-lg"
                    data-testid="buy-now-button"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Buy It Now
                  </Button>
                  
                  {/* Watch Button */}
                  <Button
                    onClick={handleWatchToggle}
                    variant="outline"
                    disabled={watchLoading}
                    className={`h-14 px-5 transition-all ${
                      isWatched 
                        ? 'border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500/20' 
                        : 'border-zinc-700 hover:border-red-500 hover:bg-red-500/10 hover:text-red-400'
                    }`}
                    data-testid="watch-button"
                  >
                    <Heart className={`w-5 h-5 ${isWatched ? 'fill-current' : ''}`} />
                  </Button>
                </div>
                
                {/* Add to Cart Button */}
                <Button 
                  onClick={handleAddToCart}
                  variant="outline"
                  className={`w-full h-12 ${itemInCart ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400' : 'border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/20 hover:border-emerald-500'}`}
                  disabled={itemInCart}
                  data-testid="add-to-cart-button"
                >
                  {itemInCart ? (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      In Cart
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5 mr-2" />
                      Add to Cart
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={handleMessageSeller}
                  variant="outline"
                  className="w-full h-12 border-zinc-700 hover:border-blue-500 hover:bg-blue-500/10"
                  disabled={messageSending}
                  data-testid="message-seller-button"
                >
                  {messageSending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Message Seller
                    </>
                  )}
                </Button>
                
                {/* Report Listing Button */}
                <Button
                  onClick={() => setReportModalOpen(true)}
                  variant="ghost"
                  className="w-full h-10 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                  data-testid="report-listing-button"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Report Listing
                </Button>
              </div>
            )}

            {isOwnCard && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <p className="text-amber-400 text-center font-medium">This is your listing</p>
                </div>
                
                {/* Owner Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={openEditModal}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="edit-listing-button"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Listing
                  </Button>
                  <Button
                    onClick={() => setDeleteDialogOpen(true)}
                    variant="outline"
                    className="border-red-600/50 text-red-400 hover:bg-red-600/20 hover:border-red-500"
                    data-testid="delete-listing-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-lg max-h-[85vh] sm:max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white font-heading text-2xl">Checkout</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Complete your purchase for {card.player_name}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 mt-4 pr-1" data-testid="checkout-scrollable-content">
            {/* Order Summary */}
            <div className="stat-card p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-400">Item Price</span>
                <span className="text-white">${card.price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Transaction Fee (3.5%)</span>
                <span className="text-white">${surcharge}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Tax (8%)</span>
                <span className="text-white">${tax}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Shipping</span>
                <span className="text-white">${shipping.toFixed(2)}</span>
              </div>
              <div className="border-t border-white/10 pt-3 flex justify-between">
                <span className="font-medium text-white">Total</span>
                <span className="font-mono font-bold text-emerald-400">${total}</span>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-4">
              <h4 className="font-medium text-white flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Shipping Address
              </h4>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-zinc-400 text-sm">Full Name</Label>
                  <Input
                    value={shippingAddress.name}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                    className="input-field mt-1"
                    placeholder="John Doe"
                    data-testid="shipping-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">Street Address</Label>
                  <Input
                    value={shippingAddress.street}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                    className="input-field mt-1"
                    placeholder="123 Main St"
                    data-testid="shipping-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-sm">City</Label>
                    <Input
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                      className="input-field mt-1"
                      placeholder="New York"
                      data-testid="shipping-city"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">State</Label>
                    <Input
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                      className="input-field mt-1"
                      placeholder="NY"
                      data-testid="shipping-state"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-sm">ZIP Code</Label>
                    <Input
                      value={shippingAddress.zip}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value })}
                      className="input-field mt-1"
                      placeholder="10001"
                      data-testid="shipping-zip"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Country</Label>
                    <Input
                      value={shippingAddress.country}
                      className="input-field mt-1"
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pay Button inside scrollable area so it's always reachable */}
            <Button
              onClick={handleCheckout}
              className="w-full btn-primary sticky bottom-0"
              disabled={checkoutLoading}
              data-testid="proceed-checkout"
            >
              {checkoutLoading ? <LoadingSpinner size="sm" /> : `Pay $${total}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-400" />
              Edit Listing
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update your listing details. Changes will be reflected immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Price Field */}
            <div>
              <Label className="text-zinc-300">Price (USD)</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  placeholder="0.00"
                  className="bg-zinc-800 border-zinc-600 pl-7 text-lg"
                  data-testid="edit-price-input"
                />
              </div>
              <p className="text-xs text-emerald-400 mt-1">You keep 100% — buyers pay the 3.5% fee</p>
            </div>
            
            {/* Description Field */}
            <div>
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Add details about your card..."
                className="bg-zinc-800 border-zinc-600 mt-1.5 h-28 resize-none"
                data-testid="edit-description-input"
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-zinc-600"
              data-testid="cancel-edit-button"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-changes-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this listing?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{card?.player_name}"? This action cannot be undone 
              and the listing will be permanently removed from the marketplace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-zinc-800 border-zinc-600 text-white hover:bg-zinc-700"
              data-testid="cancel-delete-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteListing}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-button"
            >
              {deleting ? 'Deleting...' : 'Delete Listing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Report Listing Modal */}
      <ReportListingModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        listingId={id}
        listingTitle={card ? `${card.player_name} - ${card.set_name}` : 'Loading...'}
        token={localStorage.getItem('token')}
      />
    </div>
  );
};

export default CardDetailPage;
